/**
 * Inventory — ahora la unidad visual es **la variedad**, no una fila por
 * (variety, stemLength).
 *
 * Cada tarjeta agrupa todas las longitudes de tallo registradas para esa
 * variedad, mostrando el total agregado en el header y, dentro, una tabla
 * con una fila por longitud. Cada fila conserva sus acciones `Mover` y
 * `Ajustar` que operan sobre el `id` del registro de inventario real
 * (`INV-XXX`). Una variedad puede tener múltiples INV-ids — uno por
 * longitud.
 *
 * Flujos nuevos:
 *   · "Nueva variedad" (top-level): crea la primera longitud de una
 *     variedad que no existe todavía en inventario.
 *   · "Añadir longitud" (dentro de la tarjeta): agrega otra longitud
 *     de tallo para una variedad existente.
 *
 * Endpoints:
 *   POST /api/inventory { variety, stemLength, stages? }  → crea el par
 *   POST /api/inventory/:id/move   { from, to, quantity }
 *   POST /api/inventory/:id/adjust { stage, delta }
 */
import { useMemo, useState } from 'react'
import {
  ArrowRightLeft,
  Minus,
  Package,
  Plus,
  Ruler,
} from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/ui/PageHeader'
import { ExpandableCards } from '@/components/ui/ExpandableCards'
import { Button } from '@/components/ui/Button'
import { Modal, Field, Input, Select } from '@/components/ui/Modal'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'

/**
 * Etapas visibles en la UI. Por feedback se ocultó "En proceso" — sigue
 * existiendo en el modelo de datos para no romper inventarios viejos,
 * pero no se muestra como columna ni como opción en el modal de mover.
 * "Cuarto frío" se renombró a "Procesado".
 */
const STAGES = [
  { id: 'unprocessed', label: 'Sin procesar' },
  { id: 'cold_room', label: 'Procesado' },
]

const rowTotal = (r) =>
  (r.stages?.unprocessed?.total || 0) +
  (r.stages?.in_process?.total || 0) +
  (r.stages?.cold_room?.total || 0)

const fmtQty = (n) => Number(n || 0).toLocaleString('es-CO')

export default function InventoryPage() {
  const { data, loading, error, refresh } = useApi('/inventory')
  const varieties = useApi('/varieties')
  // Doc: al "Añadir inventario" se debe poder vincular el ingreso a una
  // OC ya creada para que se autollenen los productos de esa OC.
  const purchases = useApi('/purchases')

  // Acciones por-fila (operan sobre un INV-id concreto).
  const [moving, setMoving] = useState(null)
  const [adjusting, setAdjusting] = useState(null)
  // Alta de un nuevo (variety, stemLength). Si `varietyLocked` está seteado,
  // el modal deshabilita el campo de variedad — así el botón "Añadir
  // longitud" dentro de una tarjeta no permite cambiar la variedad.
  const [creating, setCreating] = useState(false)
  const [varietyLocked, setVarietyLocked] = useState(null)
  // OC seleccionada en el modal de "Añadir inventario" — autollena los
  // items pendientes de esa orden.
  const [linkedPoId, setLinkedPoId] = useState('')
  const [receivedRows, setReceivedRows] = useState([])

  // OCs en estado activo (no cancelled, no completed) — son las únicas
  // a las que tendría sentido enlazar una recepción de inventario.
  const activePOs = useMemo(
    () =>
      (purchases.data || []).filter(
        (p) => p.status !== 'cancelled' && p.status !== 'completed',
      ),
    [purchases.data],
  )

  const linkedPo = useMemo(
    () => activePOs.find((p) => p.id === linkedPoId) || null,
    [activePOs, linkedPoId],
  )

  const move = useMutation(async (id, body) =>
    apiCall(`/inventory/${id}/move`, { method: 'POST', body }),
  )
  const adjust = useMutation(async (id, body) =>
    apiCall(`/inventory/${id}/adjust`, { method: 'POST', body }),
  )
  const create = useMutation(async (payload) =>
    apiCall('/inventory', { method: 'POST', body: payload }),
  )
  // Si se recibe menos cantidad que la OC, ajustamos la OC para que el
  // pendiente por comprar refleje el faltante. Esto es un PUT sobre items[].
  const updatePO = useMutation(async ({ id, payload }) =>
    apiCall(`/purchases/${id}`, { method: 'PUT', body: payload }),
  )
  // Marca la OC como recibida (at_farm) sin tocar inventario (ya lo
  // ajustamos manualmente acá) y dispara la transición de las ventas
  // ligadas a "En procesamiento".
  const markPOReceived = useMutation(async (id) =>
    apiCall(`/purchases/${id}/mark-received`, { method: 'POST' }),
  )

  // Agrupar por variedad. Cada grupo conserva el detalle por longitud
  // y un total agregado para mostrar en el header de la tarjeta.
  //
  // Doc: "si no hay inventario, debería desaparecer el tipo de producto y
  // todas las longitudes". Por eso ocultamos filas con total 0 y, si una
  // variedad queda sin filas, también se oculta del listado.
  const groups = useMemo(() => {
    const by = new Map()
    for (const r of data || []) {
      const key = r.variety || '—'
      if (!by.has(key)) by.set(key, [])
      by.get(key).push(r)
    }
    const out = []
    for (const [variety, allRows] of by.entries()) {
      const rows = allRows.filter((r) => rowTotal(r) > 0)
      if (rows.length === 0) continue
      rows.sort((a, b) => Number(a.stemLength) - Number(b.stemLength))
      const total = rows.reduce((acc, r) => acc + rowTotal(r), 0)
      out.push({ variety, rows, total })
    }
    out.sort((a, b) => a.variety.localeCompare(b.variety, 'es'))
    return out
  }, [data])

  const onMove = async (e) => {
    e.preventDefault()
    const fd = Object.fromEntries(new FormData(e.currentTarget).entries())
    try {
      await move.mutate(moving.id, {
        from: fd.from,
        to: fd.to,
        quantity: Number(fd.quantity),
      })
      toast.success('Movimiento registrado')
      setMoving(null)
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onAdjust = async (e) => {
    e.preventDefault()
    const fd = Object.fromEntries(new FormData(e.currentTarget).entries())
    try {
      await adjust.mutate(adjusting.id, {
        stage: fd.stage,
        delta: Number(fd.delta),
      })
      toast.success('Inventario ajustado')
      setAdjusting(null)
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  /**
   * Sube/ajusta inventario por cada fila recibida. Si la OC indicaba
   * más cantidad que la efectivamente recibida, ajustamos los items[] de
   * la OC para reflejar el faltante en el pendiente por comprar.
   */
  const submitReceiveFromPO = async () => {
    if (!linkedPo) return
    const rowsWithQty = receivedRows.filter((r) => Number(r.received) > 0)
    if (rowsWithQty.length === 0) {
      return toast.error('Indica cantidad recibida para al menos un item')
    }
    try {
      // 1) Para cada item recibido, hacer create (si no existe par
      // variety+length) o adjust (si ya existe). Lo enviamos como
      // 'cold_room' (Procesado) directamente: el doc unificó las etapas
      // y el producto recibido de OC se considera ya procesado para
      // despachar. Si después hay que mover una parte a Sin procesar
      // por algún motivo de calidad, se hace manual con "Mover".
      for (const r of rowsWithQty) {
        const existing = (data || []).find(
          (i) =>
            i.variety === r.variety &&
            Number(i.stemLength) === Number(r.stemLength),
        )
        const qty = Number(r.received) || 0
        if (existing) {
          await adjust.mutate(existing.id, { stage: 'cold_room', delta: qty })
        } else {
          await create.mutate({
            variety: r.variety,
            stemLength: Number(r.stemLength),
            stages: { cold_room: { total: qty } },
          })
        }
      }
      // 2) Si hubo faltantes (received < ordered), actualizar la OC para
      //    que la diferencia vuelva al pendiente por comprar. IMPORTANTE:
      //    consultamos `receivedRows` completo (no rowsWithQty filtrado),
      //    porque si el operario marcó received=0 para un producto que
      //    SÍ estaba en la OC, ese item debe quedar quantity=0 — no
      //    preservar la cantidad original, que daría la falsa impresión
      //    de haber llegado completo y ocultaría el faltante en el board
      //    de "Pendiente por comprar". Además guardamos `orderedQuantity`
      //    para no perder la cantidad pedida originalmente.
      const newItems = (linkedPo.items || []).map((it) => {
        const row = receivedRows.find(
          (r) =>
            r.variety === it.variety &&
            Number(r.stemLength) === Number(it.stemLength),
        )
        const ordered = Number(it.orderedQuantity ?? it.quantity) || 0
        const recv = row
          ? Math.min(Number(row.received) || 0, ordered)
          : 0
        return {
          ...it,
          orderedQuantity: ordered,
          quantity: recv,
          receivedAt: new Date().toISOString(),
        }
      })
      const shortfall = newItems.some(
        (it) =>
          Number(it.quantity) < Number(it.orderedQuantity ?? it.quantity),
      )
      if (shortfall) {
        await updatePO.mutate({
          id: linkedPo.id,
          payload: {
            items: newItems,
            partialReceivedAt: new Date().toISOString(),
          },
        })
        toast.success(
          `Recepción parcial: la OC ${linkedPo.id} se ajustó y el faltante vuelve a Pendiente por comprar.`,
        )
      } else {
        toast.success(`Recepción registrada para OC ${linkedPo.id}`)
      }
      // 3) Marcar la OC como recibida y disparar la transición de las
      //    ventas ligadas a "En procesamiento" en el dashboard. Va
      //    DESPUÉS de los adjustments para que no duplique inventario.
      try {
        await markPOReceived.mutate(linkedPo.id)
      } catch (err) {
        // No bloquear el flujo si esto falla — el inventario ya quedó
        // bien; solo no se actualizará el status del pedido.
        console.warn('mark-received failed:', err?.message)
      }
      setCreating(false)
      setLinkedPoId('')
      setReceivedRows([])
      refresh()
      purchases.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onCreate = async (e) => {
    e.preventDefault()
    // Modo OC: cuando hay una OC vinculada, ignoramos el form clásico y
    // disparamos la recepción multi-item.
    if (linkedPoId) {
      return submitReceiveFromPO()
    }

    const fd = Object.fromEntries(new FormData(e.currentTarget).entries())
    const variety = (varietyLocked || fd.variety || '').trim()
    const stemLength = Number(fd.stemLength)
    if (!variety) return toast.error('Variedad requerida')
    if (!Number.isFinite(stemLength) || stemLength <= 0) {
      return toast.error('Longitud inválida')
    }
    // Prevenir duplicados client-side (el backend también valida).
    const clash = (data || []).find(
      (r) => r.variety === variety && Number(r.stemLength) === stemLength,
    )
    if (clash) {
      return toast.error(
        `Ya existe ${variety} ${stemLength}cm — usa Ajustar para modificar`,
      )
    }
    const payload = {
      variety,
      stemLength,
      stages: {
        unprocessed: { total: Math.max(0, Number(fd.unprocessed) || 0) },
        // El cliente pidió ocultar "En proceso" — al crear, ese stage
        // siempre arranca en 0 (la migración a "Procesado" requiere el
        // workflow normal de Mover).
        in_process: { total: 0 },
        cold_room: { total: Math.max(0, Number(fd.cold_room) || 0) },
      },
    }
    try {
      await create.mutate(payload)
      toast.success(
        varietyLocked
          ? `${variety} ${stemLength}cm añadido`
          : `Variedad ${variety} creada`,
      )
      setCreating(false)
      setVarietyLocked(null)
      refresh()
      varieties.refresh()
    } catch (err) {
      if (err.message === 'duplicate_variety_length') {
        toast.error('Ese par variedad/longitud ya existe')
      } else {
        toast.error(err.message)
      }
    }
  }

  // Sugerencias de variedades: unión del catálogo remoto + lo que ya existe
  // en inventario. Evita dropdowns vacíos si la variedad aún no está en el
  // seed pero sí hay un datalist de autocompletar.
  const varietySuggestions = useMemo(() => {
    const set = new Set(varieties.data || [])
    for (const r of data || []) if (r.variety) set.add(r.variety)
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'))
  }, [varieties.data, data])

  return (
    <div>
      <PageHeader
        title="Inventario"
        subtitle="Variedades agrupadas — cada tarjeta desglosa por longitud de tallo"
        icon={Package}
        action={
          <Button
            onClick={() => {
              setVarietyLocked(null)
              setLinkedPoId('')
              setReceivedRows([])
              setCreating(true)
            }}
          >
            <Plus size={18} />
            <span>Añadir inventario</span>
          </Button>
        }
      />

      {loading && <p className="text-gray-500">Cargando…</p>}
      {error && <p className="text-red-600">Error: {error.message}</p>}

      {data && (
        <ExpandableCards
          items={groups}
          getKey={(g) => g.variety}
          emptyText="Sin inventario — empieza creando una variedad"
          renderHeader={(g) => (
            <div className="flex items-center gap-4 flex-wrap">
              <span className="font-semibold text-expo-dark">{g.variety}</span>
              <span className="text-xs text-gray-500">
                {g.rows.length} longitud{g.rows.length === 1 ? '' : 'es'}
              </span>
              <span className="ml-auto font-bold text-expo-dark">
                {fmtQty(g.total)} tallos
              </span>
            </div>
          )}
          renderDetails={(g) => (
            <div className="space-y-4 pt-4">
              {/* Desglose por longitud */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-expo-light/40 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  <div className="col-span-2">Longitud</div>
                  <div className="col-span-3 text-right">Sin procesar</div>
                  <div className="col-span-3 text-right">Procesado</div>
                  <div className="col-span-1 text-right">Total</div>
                  <div className="col-span-3 text-right">Acciones</div>
                </div>
                <div className="divide-y divide-gray-100">
                  {g.rows.map((r) => {
                    const t = rowTotal(r)
                    return (
                      <div
                        key={r.id}
                        className="grid grid-cols-12 gap-2 px-4 py-2 text-sm text-expo-dark items-center"
                      >
                        <div className="col-span-2 font-medium flex items-center gap-1">
                          <Ruler size={14} className="text-gray-400" />
                          {r.stemLength}cm
                        </div>
                        <div className="col-span-3 text-right">
                          {fmtQty(r.stages?.unprocessed?.total ?? 0)}
                        </div>
                        <div className="col-span-3 text-right">
                          {fmtQty(r.stages?.cold_room?.total ?? 0)}
                        </div>
                        <div className="col-span-1 text-right font-semibold">
                          {fmtQty(t)}
                        </div>
                        <div className="col-span-3 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setMoving(r)}
                            className="text-xs px-2 py-1 rounded-lg border border-gray-200 hover:bg-expo-light text-expo-dark inline-flex items-center gap-1"
                          >
                            <ArrowRightLeft size={12} />
                            Mover
                          </button>
                          <button
                            type="button"
                            onClick={() => setAdjusting(r)}
                            className="text-xs px-2 py-1 rounded-lg border border-gray-200 hover:bg-expo-light text-expo-dark inline-flex items-center gap-1"
                          >
                            <Plus size={10} />
                            <Minus size={10} />
                            Ajustar
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="flex">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setVarietyLocked(g.variety)
                    setLinkedPoId('')
                    setReceivedRows([])
                    setCreating(true)
                  }}
                >
                  <Plus size={16} />
                  <span>Añadir longitud a {g.variety}</span>
                </Button>
              </div>
            </div>
          )}
        />
      )}

      {/* ── Modal: Mover entre etapas ─────────────────────────────── */}
      <Modal
        open={!!moving}
        onClose={() => setMoving(null)}
        title={moving ? `Mover · ${moving.variety} ${moving.stemLength}cm` : ''}
      >
        {moving && (
          <form onSubmit={onMove} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Desde" required>
                <Select name="from" defaultValue="unprocessed" required>
                  {STAGES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label} ({fmtQty(moving.stages?.[s.id]?.total ?? 0)})
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Hasta" required>
                <Select name="to" defaultValue="cold_room" required>
                  {STAGES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="Cantidad (tallos)" required>
              <Input type="number" name="quantity" min="1" required />
            </Field>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => setMoving(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={move.loading}>
                {move.loading ? 'Moviendo…' : 'Mover'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* ── Modal: Ajustar stock ──────────────────────────────────── */}
      <Modal
        open={!!adjusting}
        onClose={() => setAdjusting(null)}
        title={adjusting ? `Ajustar · ${adjusting.variety} ${adjusting.stemLength}cm` : ''}
      >
        {adjusting && (
          <form onSubmit={onAdjust} className="space-y-4">
            <Field label="Etapa" required>
              <Select name="stage" defaultValue="unprocessed" required>
                {STAGES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label} ({fmtQty(adjusting.stages?.[s.id]?.total ?? 0)})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Delta" hint="Positivo para añadir, negativo para restar." required>
              <Input type="number" name="delta" required />
            </Field>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => setAdjusting(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={adjust.loading}>
                {adjust.loading ? 'Ajustando…' : 'Aplicar'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* ── Modal: Añadir inventario ─────────────────────────────────
       *
       * Doc: este modal sustituye al antiguo "Nueva variedad". Permite
       * dos modos:
       *   1) Vincular a OC: dropdown de OC activas → autollena los
       *      items, el usuario indica cuánto recibió por cada uno. Si
       *      recibió menos, la OC se ajusta para devolver el faltante a
       *      Pendiente por comprar.
       *   2) Manual: variedad + longitud (caso excepcional, sin OC).
       */}
      <Modal
        open={creating}
        onClose={() => {
          setCreating(false)
          setVarietyLocked(null)
          setLinkedPoId('')
          setReceivedRows([])
        }}
        title={
          varietyLocked
            ? `Añadir longitud · ${varietyLocked}`
            : 'Añadir inventario'
        }
        size={linkedPoId ? 'xl' : 'md'}
      >
        <form onSubmit={onCreate} className="space-y-4">
          {!varietyLocked && (
            <Field
              label="Vincular a orden de compra"
              hint="Selecciona una OC para autollenar los items. Deja vacío para crear manualmente."
            >
              <Select
                value={linkedPoId}
                onChange={(e) => {
                  const id = e.target.value
                  setLinkedPoId(id)
                  const po = activePOs.find((p) => p.id === id)
                  if (po) {
                    setReceivedRows(
                      (po.items || []).map((it) => ({
                        variety: it.variety,
                        stemLength: it.stemLength,
                        ordered: Number(it.quantity) || 0,
                        received: Number(it.quantity) || 0,
                      })),
                    )
                  } else {
                    setReceivedRows([])
                  }
                }}
              >
                <option value="">— sin OC (ingreso manual) —</option>
                {activePOs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.id} · {p.supplierName || '—'} · entrega{' '}
                    {p.deliveryDate || p.expectedAt || '—'}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {linkedPoId ? (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-expo-dark">
                Productos de la OC {linkedPoId}
              </div>
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-expo-light/40 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  <div className="col-span-4">Variedad</div>
                  <div className="col-span-2">Longitud</div>
                  <div className="col-span-3 text-right">Pedido</div>
                  <div className="col-span-3 text-right">Recibido</div>
                </div>
                <div className="divide-y divide-gray-100">
                  {receivedRows.map((r, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-12 gap-2 px-3 py-2 text-sm items-center"
                    >
                      <div className="col-span-4 font-medium text-expo-dark">
                        {r.variety}
                      </div>
                      <div className="col-span-2 text-gray-600">
                        {r.stemLength}cm
                      </div>
                      <div className="col-span-3 text-right text-gray-700">
                        {fmtQty(r.ordered)}
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          min="0"
                          max={r.ordered}
                          value={r.received}
                          onChange={(e) => {
                            const v = e.target.value
                            setReceivedRows((rows) =>
                              rows.map((row, idx) =>
                                idx === i ? { ...row, received: v } : row,
                              ),
                            )
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-gray-500">
                Si recibes menos cantidad de la pedida, el faltante vuelve a
                Pendiente por comprar automáticamente.
              </p>
            </div>
          ) : (
            <>
              <Field
                label="Variedad"
                required
                hint={
                  varietyLocked
                    ? 'La variedad ya existe — solo agregaremos una nueva longitud.'
                    : 'Escribe una nueva o elige una existente del autocompletar.'
                }
              >
                {varietyLocked ? (
                  <Input value={varietyLocked} readOnly disabled />
                ) : (
                  <>
                    <Input
                      name="variety"
                      list="inv-variety-suggestions"
                      placeholder="p. ej. Begonia"
                      required
                    />
                    <datalist id="inv-variety-suggestions">
                      {varietySuggestions.map((v) => (
                        <option key={v} value={v} />
                      ))}
                    </datalist>
                  </>
                )}
              </Field>
              <Field label="Longitud del tallo (cm)" required>
                <Input
                  type="number"
                  name="stemLength"
                  min="1"
                  defaultValue={60}
                  required
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sin procesar">
                  <Input type="number" name="unprocessed" min="0" defaultValue={0} />
                </Field>
                <Field label="Procesado">
                  <Input type="number" name="cold_room" min="0" defaultValue={0} />
                </Field>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setCreating(false)
                setVarietyLocked(null)
                setLinkedPoId('')
                setReceivedRows([])
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={create.loading || adjust.loading}>
              {create.loading || adjust.loading
                ? 'Guardando…'
                : linkedPoId
                  ? 'Registrar recepción'
                  : varietyLocked
                    ? 'Añadir longitud'
                    : 'Añadir inventario'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
